import { useMemo } from 'react';
import { authorImageUrl, coverUrl, highResCoverUrl } from '../api/abs';
import {
  FINISHED_THRESHOLD,
  isLibraryItem,
  itemSizeBytes,
  parseEpochMs,
  parseLenientBool,
  technicalMetadataOf,
} from '../api/helpers';
import { progressStore } from '../api/progressStore';
import { getSession, sessionStore } from '../api/session';
import type { Author, Collection, LibraryItem, MediaProgress, Series } from '../api/types';
import type { DownloadRecord, DownloadSummary } from '../downloads/manager';
import { downloadsStore, serverCoverFromItem } from '../downloads/manager';
import { createStore, useStore } from '../lib/store';
import type { ReadingListBook } from './readingList';

// The shapes the UI renders. Every book on every screen is a `BookSummary`,
// whatever it came from (ABS item, local library cache, download record,
// reading-list snapshot). Parsing follows Flutter's `Book.fromJson`
// (lib/models/book.dart:149-712), `Series.fromJson` and `Author.fromJson`.

export { coverUrl, highResCoverUrl, authorImageUrl };
export { FINISHED_THRESHOLD };

export const UNKNOWN_TITLE = 'Unknown Title';
export const UNKNOWN_AUTHOR = 'Unknown Author';
export const UNKNOWN_SERIES = 'Unknown Series';

export interface PersonRef {
  /** `''` when the server sent only a name (minified items). */
  id: string;
  name: string;
}

export interface SeriesMembership {
  /** `''` when the server sent only a name (minified items). */
  id: string;
  name: string;
  sequence: string | null;
}

export interface BookSummary {
  kind: 'book';
  id: string;
  libraryId: string;
  title: string;
  subtitle: string | null;
  /** Display author line; `Unknown Author` when missing. */
  authorName: string;
  authors: PersonRef[];
  /** Narrators joined with `, ` (`''` when none). */
  narratorName: string;
  narrators: string[];
  /** First series name, without sequence. */
  seriesName: string | null;
  series: SeriesMembership[];
  /** Seconds. */
  duration: number;
  publishedYear: number | null;
  publisher: string | null;
  genres: string[];
  tags: string[];
  /** Raw server description (may contain HTML). */
  description: string;
  /** Epoch ms (0 when unknown). */
  addedAt: number;
  /** Epoch ms. */
  updatedAt: number | null;
  /** False only when the server says there is no cover. */
  hasCover: boolean;
  /** 0..1 (1 when finished). */
  progress: number;
  /** Seconds listened. */
  currentTime: number;
  isFinished: boolean;
  /** Epoch ms. */
  finishedAt: number | null;
  /** Epoch ms of the last progress update ("Last Played"). */
  lastUpdate: number | null;
  hideFromContinueListening: boolean;
  hasEbook: boolean;
  explicit: boolean;
  isDownloaded: boolean;
  numChapters: number;
  sizeBytes: number | null;
  /** Bits per second as ABS reports it. */
  bitrate: number | null;
  codec: string | null;
  container: string | null;
  isMissing: boolean;
}

export interface SeriesSummary {
  kind: 'series';
  id: string;
  name: string;
  description: string | null;
  /** Ordered ids from the list payload (Flutter `libraryItemIds`, used for `#n` badges). */
  libraryItemIds: string[];
  /** `libraryItemIds.length`, or the server's `numBooks` when no ids came back. */
  bookCount: number;
  /** Author of the first embedded book (`''` when unknown). */
  authorName: string;
  /** Epoch ms: `addedAt ?? lastUpdate ?? createdAt ?? 0`. */
  addedAt: number;
  /** Server `progress.isFinished`. */
  isFinished: boolean;
  /** Server `progress.libraryItemIdsFinished`. */
  finishedItemIds: string[];
  /** Embedded books (empty when the payload had none). */
  books: BookSummary[];
}

export interface CollectionSummary extends Omit<SeriesSummary, 'kind'> {
  kind: 'collection';
}

export type GroupSummary = SeriesSummary | CollectionSummary;

export interface AuthorSummary {
  kind: 'author';
  id: string;
  name: string;
  description: string | null;
  imagePath: string | null;
  numBooks: number;
  /** Epoch ms (0 when unknown). */
  addedAt: number;
}

export type ShelfItem = BookSummary | SeriesSummary;

export interface HomeShelf {
  id: string;
  label: string;
  items: ShelfItem[];
}

// ---------------------------------------------------------------- parsing helpers

type Bag = Record<string, unknown>;

const bag = (v: unknown): Bag | undefined => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Bag) : undefined);

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : '');

const textOrNull = (v: unknown): string | null => text(v) || null;

const num = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n);

function stringList(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map((x) => text(typeof x === 'object' ? (bag(x)?.name ?? bag(x)?.narrator) : x)).filter(Boolean);
}

function splitNames(v: unknown): string[] {
  return text(v)
    .split(/\s*,\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function parsePeople(v: unknown): PersonRef[] {
  if (!Array.isArray(v)) return [];
  const out: PersonRef[] = [];
  for (const raw of v) {
    if (typeof raw === 'string') {
      if (raw.trim()) out.push({ id: '', name: raw.trim() });
      continue;
    }
    const b = bag(raw);
    if (!b) continue;
    const name = text(b.name ?? b.authorName);
    const id = text(b.id ?? b.authorId);
    if (name || id) out.push({ id, name: name || UNKNOWN_AUTHOR });
  }
  return out;
}

function parseSeriesEntry(raw: unknown): SeriesMembership | null {
  if (typeof raw === 'string') return raw.trim() ? { id: '', name: raw.trim(), sequence: null } : null;
  const b = bag(raw);
  if (!b) return null;
  const id = text(b.id ?? b.seriesId);
  const name = text(b.name ?? b.seriesName);
  if (!id && !name) return null;
  return { id, name, sequence: textOrNull(b.sequence) };
}

/** `"Name #2, Other #1"` (minified items) → memberships without ids. */
function parseSeriesName(v: unknown): SeriesMembership[] {
  return splitNames(v).map((part) => {
    const m = /^(.*\S)\s+#(\S+)$/.exec(part);
    return m ? { id: '', name: m[1] as string, sequence: m[2] as string } : { id: '', name: part, sequence: null };
  });
}

function parseSeries(item: Bag, metadata: Bag | undefined): SeriesMembership[] {
  for (const source of [metadata?.series, item.series, metadata?.seriesList]) {
    const list = Array.isArray(source) ? source : source != null ? [source] : [];
    const parsed = list.map(parseSeriesEntry).filter((s): s is SeriesMembership => s !== null);
    if (parsed.length > 0) return parsed;
  }
  const id = text(item.seriesId ?? metadata?.seriesId);
  const nameRaw = item.seriesName ?? metadata?.seriesName;
  const fromName = parseSeriesName(nameRaw);
  if (fromName.length > 0) {
    if (id && fromName[0]) fromName[0] = { ...fromName[0], id };
    return fromName;
  }
  return id ? [{ id, name: '', sequence: null }] : [];
}

function parseAuthorName(item: Bag, metadata: Bag | undefined, authors: PersonRef[]): string {
  let author = text(metadata?.authorName) || text(metadata?.author);
  if (!author && Array.isArray(metadata?.authors)) {
    author = (metadata.authors as unknown[])
      .map((a) => (typeof a === 'string' ? a.trim() : text(bag(a)?.name)))
      .filter(Boolean)
      .join(', ');
  }
  if (!author || author === UNKNOWN_AUTHOR) author = authors[0]?.name ?? '';
  if (!author || author === UNKNOWN_AUTHOR) {
    if (typeof item.numBooks === 'number') author = `${item.numBooks} Books`;
    else author = text(bag((item.series as unknown[] | undefined)?.[0])?.name);
  }
  return author || UNKNOWN_AUTHOR;
}

const PROGRESS_KEYS = ['currentTime', 'isFinished', 'finishedAt', 'progress'];

function asProgress(candidate: unknown): Bag | undefined {
  const b = bag(candidate);
  if (!b) return undefined;
  if (PROGRESS_KEYS.some((k) => k in b)) return b;
  for (const key of ['userMediaProgress', 'mediaProgress', 'progress']) {
    const nested = bag(b[key]);
    if (nested && PROGRESS_KEYS.some((k) => k in nested)) return nested;
  }
  return undefined;
}

function embeddedProgress(item: Bag, media: Bag | undefined): Bag | undefined {
  for (const c of [item.userMediaProgress, item.mediaProgress, item.progress, item.userProgress, media?.progress, media?.userMediaProgress]) {
    const p = asProgress(c);
    if (p) return p;
  }
  return undefined;
}

/** Newest of two progress entries by `lastUpdate` (an entry without one loses to one with it). */
function newestProgress(a: Bag | undefined, b: Bag | undefined): Bag | undefined {
  if (!a) return b;
  if (!b) return a;
  return (num(b.lastUpdate) ?? 0) > (num(a.lastUpdate) ?? 0) ? b : a;
}

interface ProgressFields {
  currentTime: number;
  progress: number;
  isFinished: boolean;
  finishedAt: number | null;
  lastUpdate: number | null;
  hideFromContinueListening: boolean;
}

/** Flutter's progress rules: finished by flag, by `finishedAt`, or at ≥ 98 % of the duration. */
function progressFields(p: Bag | undefined, duration: number): ProgressFields {
  if (!p) return { currentTime: 0, progress: 0, isFinished: false, finishedAt: null, lastUpdate: null, hideFromContinueListening: false };
  const ct = num(p.currentTime) ?? 0;
  const ratio = num(p.progress) ?? 0;
  const currentTime = ct > 0 ? ct : ratio > 0 && ratio <= 1 && duration > 0 ? duration * ratio : 0;
  const finishedAt = parseEpochMs(p.finishedAt);
  let isFinished = parseLenientBool(p.isFinished) ?? false;
  if (!isFinished && finishedAt != null) isFinished = true;
  if (!isFinished && duration > 0 && currentTime / duration >= FINISHED_THRESHOLD) isFinished = true;
  const progress = isFinished ? 1 : duration > 0 ? clamp01(currentTime / duration) : clamp01(ratio);
  return {
    currentTime,
    progress,
    isFinished,
    finishedAt,
    lastUpdate: parseEpochMs(p.lastUpdate),
    hideFromContinueListening: p.hideFromContinueListening === true,
  };
}

// ---------------------------------------------------------------- books

export interface BookMapOptions {
  /** The user's progress entry (from `progressStore`); the newer of it and the embedded one wins. */
  progress?: MediaProgress;
  isDownloaded?: boolean;
}

/** ABS library item → `BookSummary` (port of Flutter `Book.fromJson`). */
export function bookFromLibraryItem(item: LibraryItem, opts: BookMapOptions = {}): BookSummary {
  const raw = item as unknown as Bag;
  const media = bag(raw.media);
  const metadata = bag(media?.metadata);
  const duration = num(media?.duration) ?? 0;
  const authors = parsePeople(metadata?.authors).length > 0 ? parsePeople(metadata?.authors) : parsePeople(raw.authors);
  const authorName = parseAuthorName(raw, metadata, authors);
  const resolvedAuthors = authors.length > 0 ? authors : authorName === UNKNOWN_AUTHOR ? [] : splitNames(authorName).map((name) => ({ id: '', name }));
  let narrators = stringList(metadata?.narrators);
  if (narrators.length === 0) narrators = splitNames(metadata?.narratorName ?? metadata?.narrator);
  const series = parseSeries(raw, metadata);
  const tech = technicalMetadataOf(item);
  const p = progressFields(newestProgress(embeddedProgress(raw, media), opts.progress as Bag | undefined), duration);
  const chapters = Array.isArray(media?.chapters) ? media.chapters.length : 0;
  return {
    kind: 'book',
    id: String(raw.id ?? ''),
    libraryId: text(raw.libraryId),
    title: text(metadata?.title) || text(raw.name) || UNKNOWN_TITLE,
    subtitle: textOrNull(metadata?.subtitle),
    authorName,
    authors: resolvedAuthors,
    narratorName: narrators.join(', '),
    narrators,
    seriesName: series.find((s) => s.name)?.name ?? null,
    series,
    duration,
    publishedYear: num(metadata?.publishedYear) != null ? Math.trunc(num(metadata?.publishedYear) as number) : null,
    publisher: textOrNull(metadata?.publisher),
    genres: stringList(metadata?.genres),
    tags: stringList(media?.tags),
    description: text(metadata?.description) || text(raw.description),
    addedAt: parseEpochMs(raw.addedAt) ?? 0,
    updatedAt: parseEpochMs(raw.updatedAt),
    hasCover: media?.coverPath !== null,
    ...p,
    hasEbook: !!media?.ebookFile || !!text(media?.ebookFormat),
    explicit: metadata?.explicit === true,
    isDownloaded: opts.isDownloaded ?? false,
    numChapters: chapters || (num(media?.numChapters) ?? 0),
    sizeBytes: itemSizeBytes(item),
    bitrate: tech.bitrate,
    codec: tech.codec,
    container: tech.container,
    isMissing: raw.isMissing === true || raw.isInvalid === true,
  };
}

/** Fills every field of a partial book (reading-list snapshots, previews). */
export function normalizeBookSummary(partial: Partial<BookSummary> & Pick<BookSummary, 'id'>): BookSummary {
  return {
    kind: 'book',
    libraryId: '',
    title: UNKNOWN_TITLE,
    subtitle: null,
    authorName: UNKNOWN_AUTHOR,
    authors: [],
    narratorName: '',
    narrators: [],
    seriesName: null,
    series: [],
    duration: 0,
    publishedYear: null,
    publisher: null,
    genres: [],
    tags: [],
    description: '',
    addedAt: 0,
    updatedAt: null,
    hasCover: true,
    progress: 0,
    currentTime: 0,
    isFinished: false,
    finishedAt: null,
    lastUpdate: null,
    hideFromContinueListening: false,
    hasEbook: false,
    explicit: false,
    isDownloaded: false,
    numChapters: 0,
    sizeBytes: null,
    bitrate: null,
    codec: null,
    container: null,
    isMissing: false,
    ...partial,
  };
}

/**
 * Applies a progress entry when it is at least as new as what the book has.
 * Used to join `progressStore` into books that were parsed earlier.
 */
export function withProgress(book: BookSummary, progress: MediaProgress | undefined): BookSummary {
  if (!progress) return book;
  const incomingAt = parseEpochMs(progress.lastUpdate) ?? 0;
  if (book.lastUpdate != null && incomingAt < book.lastUpdate) return book;
  const p = progressFields(progress as unknown as Bag, book.duration);
  if (
    p.currentTime === book.currentTime &&
    p.isFinished === book.isFinished &&
    p.finishedAt === book.finishedAt &&
    p.lastUpdate === book.lastUpdate &&
    p.hideFromContinueListening === book.hideFromContinueListening
  ) {
    return book;
  }
  return { ...book, ...p };
}

/**
 * Whether the server has a cover for a downloaded book: a cover stored on
 * this device proves it; otherwise the record's (or its stored item's)
 * answer; unknown counts as yes, like `BookSummary.hasCover` ("false only when
 * the server says there is no cover"), so the cover is still tried online.
 */
export function downloadHasCover(record: DownloadRecord | DownloadSummary): boolean {
  const stored = 'hasCover' in record ? record.hasCover : !!record.coverBlob;
  if (stored) return true;
  if (record.serverHasCover !== undefined) return record.serverHasCover;
  return ('item' in record ? serverCoverFromItem(record.item) : undefined) ?? true;
}

/** Download record → book (works offline; uses the stored item when present). */
export function bookFromDownload(record: DownloadRecord | DownloadSummary, progress?: MediaProgress): BookSummary {
  const fromItem = 'item' in record && record.item && isLibraryItem(record.item) ? bookFromLibraryItem(record.item, { progress, isDownloaded: true }) : null;
  const hasCover = downloadHasCover(record);
  const base =
    fromItem ??
    normalizeBookSummary({
      id: record.itemId,
      libraryId: record.libraryId ?? '',
      title: record.title || UNKNOWN_TITLE,
      subtitle: record.subtitle ?? null,
      authorName: record.author || UNKNOWN_AUTHOR,
      authors: splitNames(record.author).map((name) => ({ id: '', name })),
      narratorName: record.narrator ?? '',
      narrators: splitNames(record.narrator),
      seriesName: record.seriesName ?? null,
      series: record.seriesName ? [{ id: '', name: record.seriesName, sequence: null }] : [],
      duration: record.duration,
      publishedYear: record.publishedYear ?? null,
      genres: record.genres ?? [],
      addedAt: record.addedAt ?? 0,
      updatedAt: record.updatedAt ?? null,
      hasCover,
      numChapters: record.chapters.length,
    });
  const book: BookSummary = {
    ...base,
    hasCover,
    isDownloaded: record.status === 'complete',
    sizeBytes: record.sizeBytes ?? (record.totalBytes > 0 ? record.totalBytes : base.sizeBytes),
    codec: record.codec ?? base.codec,
    container: record.container ?? base.container,
    bitrate: record.bitrate ?? base.bitrate,
  };
  return fromItem ? book : withProgress(book, progress);
}

/** Flutter `totalDuration` of a reading-list snapshot: the duration, else the sum of the chapter lengths (0 when neither is known). */
export function readingListTotalDuration(book: Pick<ReadingListBook, 'duration' | 'chapters'>): number {
  if (book.duration > 0) return book.duration;
  return book.chapters.reduce((sum, chapter) => sum + Math.max(0, chapter.end - chapter.start), 0);
}

/**
 * Reading-list snapshot (Flutter codec shape) → `BookSummary`. `lastUpdate`
 * stays null so any live progress entry for the book wins over the snapshot
 * (`withProgress`), which keeps rows current with playback.
 */
export function bookFromReadingList(book: ReadingListBook, libraryId = ''): BookSummary {
  const total = readingListTotalDuration(book);
  const progress = total > 0 ? Math.min(1, Math.max(0, book.userProgress / total)) : 0;
  const narrators = book.narrators;
  return normalizeBookSummary({
    id: book.id,
    libraryId,
    title: book.title,
    authorName: book.author,
    authors: book.author ? [{ id: book.authorId ?? '', name: book.author }] : [],
    narrators,
    narratorName: narrators.join(', '),
    seriesName: book.seriesName,
    series: book.seriesId ? [{ id: book.seriesId, name: book.seriesName ?? '', sequence: null }] : [],
    duration: book.duration,
    publishedYear: book.publishedYear,
    publisher: book.publisher || null,
    genres: book.genres,
    description: book.description,
    addedAt: parseEpochMs(book.addedAt) ?? 0,
    updatedAt: parseEpochMs(book.updatedAt),
    progress,
    currentTime: book.userProgress,
    isFinished: book.isFinished,
    finishedAt: parseEpochMs(book.finishedAt),
    lastUpdate: null,
    isDownloaded: book.isDownloaded,
    numChapters: book.chapters.length,
    sizeBytes: book.sizeInBytes,
    bitrate: book.fileBitrateKbps != null ? book.fileBitrateKbps * 1000 : null,
    codec: book.fileCodec,
    container: book.fileContainer,
  });
}

/** Download ids playable offline for the signed-in account. */
export function downloadedIds(downloads: Record<string, DownloadSummary>, session = getSession()): Set<string> {
  const ids = new Set<string>();
  for (const d of Object.values(downloads)) {
    if (d.status === 'complete' && (!session || (d.serverUrl === session.serverUrl && d.userId === session.userId))) ids.add(d.itemId);
  }
  return ids;
}

/** Joins live progress and download state into a book (pure; see `useLiveBook`). */
export function liveBook(book: BookSummary, progress: Record<string, MediaProgress>, downloaded: Set<string>): BookSummary {
  const joined = withProgress(book, progress[book.id]);
  const isDownloaded = downloaded.has(book.id);
  return joined.isDownloaded === isDownloaded ? joined : { ...joined, isDownloaded };
}

// The signed-in account's downloaded ids as a store whose Set only changes
// when its content does: download progress ticks (4 per second) must not
// re-run every memo that depends on it.
const downloadedIdsStore = createStore<Set<string>>(new Set());
let downloadedIdsKey = '';

function refreshDownloadedIds(): void {
  const ids = downloadedIds(downloadsStore.get(), getSession());
  const key = [...ids].sort().join('\n');
  if (key === downloadedIdsKey) return;
  downloadedIdsKey = key;
  downloadedIdsStore.set(ids);
}

downloadsStore.subscribe(refreshDownloadedIds);
sessionStore.subscribe(refreshDownloadedIds);
refreshDownloadedIds();

/** Ids the signed-in account can play offline; the same Set until that list changes. */
export function getDownloadedIds(): Set<string> {
  return downloadedIdsStore.get();
}

/** `getDownloadedIds()` as a hook (stable identity between download progress ticks). */
export function useDownloadedIds(): Set<string> {
  return useStore(downloadedIdsStore);
}

/**
 * A book with live progress (mark finished, playback) and download state joined in.
 * Subscribes to this book's progress entry and download flag only, so a tile
 * does not re-render on every progress patch of the book that is playing.
 */
export function useLiveBook<T extends BookSummary | null | undefined>(book: T): T {
  const id = book?.id;
  const entry = useStore(progressStore, (all) => (id ? all[id] : undefined));
  const isDownloaded = useStore(downloadedIdsStore, (ids) => (id ? ids.has(id) : false));
  return useMemo(() => {
    if (!book) return book;
    const joined = withProgress(book, entry);
    return (joined.isDownloaded === isDownloaded ? joined : { ...joined, isDownloaded }) as T;
  }, [book, entry, isDownloaded]);
}

/** `useLiveBook` for a list; returns the same array when nothing changed. */
export function useLiveBooks(books: readonly BookSummary[]): BookSummary[] {
  const progress = useStore(progressStore);
  const downloaded = useDownloadedIds();
  return useMemo(() => {
    let changed = false;
    const next = books.map((b) => {
      const live = liveBook(b, progress, downloaded);
      if (live !== b) changed = true;
      return live;
    });
    return changed ? next : (books as BookSummary[]);
  }, [books, progress, downloaded]);
}

// ---------------------------------------------------------------- book helpers

/** Progress ratio using the chapter sum when the duration is missing (`bookProgressFraction`). */
export function bookProgressFraction(book: Pick<BookSummary, 'currentTime' | 'duration' | 'progress'>): number {
  if (book.duration > 0) return clamp01(book.currentTime / book.duration);
  return clamp01(book.progress);
}

/** `isBookEffectivelyFinished`: the flag, `finishedAt`, or ≥ 98 %. */
export function isBookEffectivelyFinished(book: Pick<BookSummary, 'isFinished' | 'finishedAt' | 'currentTime' | 'duration' | 'progress'>): boolean {
  return book.isFinished || (book.finishedAt ?? 0) > 0 || bookProgressFraction(book) >= FINISHED_THRESHOLD;
}

export function primaryAuthor(book: BookSummary): PersonRef | null {
  return book.authors.find((a) => a.id) ?? null;
}

export function primarySeries(book: BookSummary): SeriesMembership | null {
  return book.series.find((s) => s.id && s.name) ?? null;
}

/** Series row label for quick actions: needs a series id and name. */
export function quickActionSeriesLabel(book: BookSummary): string | null {
  return primarySeries(book)?.name.trim() || null;
}

/** Cover URL for a book, `''` when the server has no cover. */
export function bookCoverUrl(book: Pick<BookSummary, 'id' | 'updatedAt' | 'hasCover'>, width = 400): string {
  if (!book.hasCover) return '';
  return coverUrl({ id: book.id, updatedAt: book.updatedAt ?? undefined }, width);
}

// ---------------------------------------------------------------- series / collections

function seriesBase(raw: Bag): Omit<SeriesSummary, 'kind'> {
  const nested = bag(raw.series);
  const id = text(nested?.id ?? raw.id);
  const name = text(nested?.name) || text(raw.name) || text(raw.title) || UNKNOWN_SERIES;
  const list = (Array.isArray(raw.books) ? raw.books : Array.isArray(raw.libraryItems) ? raw.libraryItems : Array.isArray(raw.items) ? raw.items : []) as unknown[];
  const ids: string[] = [];
  const books: BookSummary[] = [];
  let authorName = '';
  for (const entry of list) {
    const b = bag(entry);
    if (!b) continue;
    const libraryItem = bag(b.libraryItem);
    const bookId = 'libraryItem' in b ? text(libraryItem?.id ?? b.libraryItem) : text(b.id);
    if (!bookId) continue;
    if (!ids.includes(bookId)) ids.push(bookId);
    const item = libraryItem && bag(libraryItem.media) ? libraryItem : bag(b.media) ? b : undefined;
    if (item) {
      const book = bookFromLibraryItem(item as unknown as LibraryItem);
      books.push(book);
      if (!authorName && book.authorName !== UNKNOWN_AUTHOR) authorName = book.authorName;
    }
  }
  const progress = bag(raw.progress);
  const fallbackIds = [progress?.libraryItemIds, raw.libraryItemIds].find((v) => Array.isArray(v) && v.length > 0) as unknown[] | undefined;
  const libraryItemIds = ids.length > 0 ? ids : (fallbackIds ?? []).map(text).filter(Boolean);
  const finishedItemIds = Array.isArray(progress?.libraryItemIdsFinished) ? (progress.libraryItemIdsFinished as unknown[]).map(text).filter(Boolean) : [];
  const finished = new Set(finishedItemIds);
  return {
    id,
    name,
    description: textOrNull(raw.description),
    libraryItemIds,
    bookCount: libraryItemIds.length > 0 ? libraryItemIds.length : (num(raw.numBooks) ?? 0),
    authorName,
    addedAt: parseEpochMs(raw.addedAt ?? raw.lastUpdate ?? raw.createdAt) ?? 0,
    isFinished: progress?.isFinished === true,
    finishedItemIds,
    books: books.map((b) => (finished.has(b.id) && !b.isFinished ? { ...b, isFinished: true, progress: 1 } : b)),
  };
}

/** ABS series (list, detail or search shape) → `SeriesSummary` (Flutter `Series.fromJson`). */
export function seriesFromAbs(series: Series): SeriesSummary {
  return { kind: 'series', ...seriesBase(series as unknown as Bag) };
}

/** ABS collection → `CollectionSummary` (Flutter models collections as series). */
export function collectionFromAbs(collection: Collection): CollectionSummary {
  return { kind: 'collection', ...seriesBase(collection as unknown as Bag) };
}

/** `bookCount > 0 ? bookCount : libraryItemIds.length` (list sort "Most Books"). */
export function groupBookCount(group: Pick<SeriesSummary, 'bookCount' | 'libraryItemIds'>): number {
  return group.bookCount > 0 ? group.bookCount : group.libraryItemIds.length;
}

/** Cover URLs for the fanned art stack (first `max` books). */
export function groupCoverUrls(group: Pick<SeriesSummary, 'libraryItemIds' | 'books'>, width = 300, max = 6): string[] {
  const updated = new Map(group.books.map((b) => [b.id, b] as const));
  return group.libraryItemIds.slice(0, max).map((id) => {
    const book = updated.get(id);
    return book ? bookCoverUrl(book, width) : coverUrl(id, width);
  });
}

// ---------------------------------------------------------------- authors

/** ABS author → `AuthorSummary` (Flutter `Author.fromJson`). */
export function authorFromAbs(author: Author): AuthorSummary {
  const raw = author as unknown as Bag;
  return {
    kind: 'author',
    id: text(raw.id),
    name: text(raw.name) || UNKNOWN_AUTHOR,
    description: textOrNull(raw.description),
    imagePath: textOrNull(raw.imagePath),
    numBooks: Math.trunc(num(raw.numBooks) ?? 0),
    addedAt: parseEpochMs(raw.addedAt) ?? 0,
  };
}

/** Author photo URL, `''` when the author has no image (show the person glyph). */
export function authorPhotoUrl(author: Pick<AuthorSummary, 'id' | 'imagePath'>, width = 200): string {
  return author.imagePath ? authorImageUrl({ id: author.id, imagePath: author.imagePath }, width) : '';
}
